import request from 'superagent';

const ENDPOINT = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

/**
 * Fetch Player Rankings contents for a given year
 * @param {*} year ID of team to fetch
 */
export async function fetchPlayerRankings(year) {
  var queryUri = null;
  if (year) {
    queryUri = encodeURI(`${ENDPOINT}/playerRankings/${year}`);
  } else {
    queryUri = encodeURI(`${ENDPOINT}/playerRankings/${new Date().getFullYear()}`);
  }

  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return [];
  }
}

/**
 * Find players
 * @param {string} searchTerm Search term to use
 */
export async function findPlayers(searchTerm) {
  const queryUri = encodeURI(`${ENDPOINT}/players/search/${searchTerm}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res.body;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch player profile data
 * @param {string} playerId ID of player to fetch
 */
export async function getPlayerProfile(playerId) {
  const queryUri = encodeURI(`${ENDPOINT}/players/profile/${playerId}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res.body;
  } catch (error) {
    return error;
  }
}

/**
 * Edit player info
 * @param {string} playerId ID of player to edit
 * @param {object} playerInfo Object containing modified player info
 */
export async function editPlayer({
  playerId,
  editedPlayerInfo
}) {
  if (process.env.REACT_APP_ENVIRONMENT !== "local") {
    return 403;
  }
  const queryUri = encodeURI(`${ENDPOINT}/players/updatePlayerInfo/`);
  try {
    const res = await request
      .post(queryUri)
      .send({
        playerId: playerId,
        playerInfo: editedPlayerInfo
      })
      .set('Content-Type', 'application/json')
      .set('x-api-key', API_KEY);
    return res.statusCode;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch player stats by series
 * @param {string} playerId ID of player to fetch
 * @param {string} sinceDate string of furthest date to start from, default Jan 1 2023
 */
export async function getPlayerStatsBySeries(playerId, sinceDate) {
  const queryUri = encodeURI(`${ENDPOINT}/players/statsBySeries/${playerId}`);
  try {
    const res = await request
      .get(queryUri)
      .query({
        sinceDate: sinceDate
      })
      .set('x-api-key', API_KEY);
    return res.body;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch player stats by split
 * @param {string} playerId ID of player to fetch
 */
export async function getPlayerStatsBySplit(playerId) {
  const queryUri = encodeURI(`${ENDPOINT}/players/statsBySplit/${playerId}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    console.log(res.body)
    return res.body;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch player match history
 * @param {string} playerId ID of player to fetch
 */
export async function getPlayerMatchHistory(playerId) {
  const queryUri = encodeURI(`${ENDPOINT}/players/matchHistory/${playerId}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res.body;
  } catch (error) {
    return error;
  }
}

/**
 * Update player rankings info
 * @param {string} playerId ID of player to update
 * @param {string} offseasonYear offseason year to update
 * @param {string} updateField field to update
 * @param {string} updateValue value to set on this field
 */
export async function updatePlayerRankingsPlayerInfo({
  playerId,
  offseasonYear,
  updateField,
  updateValue
}) {
  if (process.env.REACT_APP_ENVIRONMENT !== "local") {
    return 403;
  }
  const queryUri = encodeURI(`${ENDPOINT}/players/updatePlayerStatus/`);
  try {
    const res = await request
      .post(queryUri)
      .send({
        playerId: playerId,
        offseasonYear: offseasonYear,
        updateField: updateField,
        updateValue: updateValue
      })
      .set('Content-Type', 'application/json')
      .set('x-api-key', API_KEY);
    return res.statusCode;
  } catch (error) {
    return error;
  }
}

/**
 * Query the current stats definitions.
 */
export async function getStatsDefinitions() {
  const queryUri = encodeURI(`${ENDPOINT}/definitions`)
  try {
    const res = await request
      .get(queryUri)
      .set('X-Api-Key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch a tournament's details
 */
export async function getTournamentInfo(id) {
  const queryUri = encodeURI(`${ENDPOINT}/tournaments/${id}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch leagues that have stats
 */
export async function getStatsLeagues() {
  const queryUri = encodeURI(`${ENDPOINT}/stats/leagues`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch tournaments that have stats
 */
export async function getStatsTournaments() {
  const queryUri = encodeURI(`${ENDPOINT}/stats/tournaments`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch quick access tournament stats links
 */
export async function getQuickAccessTournaments() {
  const queryUri = encodeURI(`${ENDPOINT}/tournaments/quickAccess`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch player stats for a given tournament
 * @param {string} tournamentId ID of tournament to fetch
 * TODO: Add params for things like data ranges, map, agent, etc.
 */
export async function getTournamentPlayerStats({ tournamentId, mapId, agentId }) {
  const queryUri = encodeURI(`${ENDPOINT}/stats/players/byTournament/${tournamentId}`);
  try {
    const res = await request
      .get(queryUri)
      .query({
        // fromDate: fromDate
        // toDate: toDate
        mapId: mapId,
        agentId: agentId
      })
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch upcoming matches
 * @param {string} type OPTIONAL: type of entity to fetch for (team or player)
 * @param {string} id OPTIONAL: id of entity to fetch for
 */
export async function getUpcomingMatches({ type, id }) {
  const forString = type ? `?for=${type}` : '';
  const idString = id ? `&id=${id}` : '';
  const queryUri = encodeURI(`${ENDPOINT}/matches/upcoming${forString}${idString}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch in-progress matches
 */
export async function getInProgressMatches() {
  const queryUri = encodeURI(`${ENDPOINT}/matches/inProgress`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch recent matches
 * @param {string} type OPTIONAL: type of entity to fetch for (team or player)
 * @param {string} id OPTIONAL: id of entity to fetch for
 */
export async function getRecentMatches({ type, id }) {
  const forString = type ? `?for=${type}` : '';
  const idString = id ? `&id=${id}` : '';
  const queryUri = encodeURI(`${ENDPOINT}/matches/recent${forString}${idString}`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch team stats for a given tournament
 * @param {string} tournamentId ID of tournament to fetch
 * TODO: Add params for things like data ranges, map, etc.
 */
export async function getTournamentTeamStats({ tournamentId, mapId }) {
  const queryUri = encodeURI(`${ENDPOINT}/stats/teams/byTournament/${tournamentId}`);
  try {
    const res = await request
      .get(queryUri)
      .query({
        // fromDate: fromDate
        // toDate: toDate
        mapId: mapId,
      })
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch list of maps
 */
export async function getMaps() {
  const queryUri = encodeURI(`${ENDPOINT}/metadata/maps`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch list of agents
 */
export async function getAgents() {
  const queryUri = encodeURI(`${ENDPOINT}/metadata/agents`);
  try {
    const res = await request
      .get(queryUri)
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    return error;
  }
}

/**
 * Add note
 * @param {string} entityId ID of entity on which to create note
 * @param {string} entityType entity type (player, team)
 * @param {object} noteObject note object
 */
export async function addNote({
  entityId,
  entityType,
  noteObject
}) {
  if (process.env.REACT_APP_ENVIRONMENT !== "local") {
    return 403;
  }
  const queryUri = encodeURI(`${ENDPOINT}/notes/addNote/`);
  try {
    const res = await request
      .post(queryUri)
      .send({
        entityId: entityId,
        entityType: entityType,
        noteObject: JSON.stringify(noteObject)
      })
      .set('Content-Type', 'application/json')
      .set('x-api-key', API_KEY);
    return res.statusCode;
  } catch (error) {
    return error;
  }
}

/**
 * Delete note
 * @param {string} entityId ID of entity on which to delete note
 * @param {string} entityType entity type (player, team)
 * @param {string} noteUuid note uuid
 */
export async function deleteNote({
  entityId,
  entityType,
  noteUuid
}) {
  if (process.env.REACT_APP_ENVIRONMENT !== "local") {
    return 403;
  }
  const queryUri = encodeURI(`${ENDPOINT}/notes/deleteNote/`);
  try {
    const res = await request
      .post(queryUri)
      .send({
        entityId: entityId,
        entityType: entityType,
        uuid: noteUuid
      })
      .set('Content-Type', 'application/json')
      .set('x-api-key', API_KEY);
    return res.statusCode;
  } catch (error) {
    return error;
  }
}

/**
 * Fetch scrim history
 * @param {string} sinceDate string of furthest date to start from, default Jan 1 2023
 */
export async function fetchScrimHistory(sinceDate) {
  const queryUri = encodeURI(`${ENDPOINT}/scrims/matchHistory`);

  try {
    const res = await request
      .get(queryUri)
      .query({
        sinceDate: sinceDate
      })
      .set('x-api-key', API_KEY);
    return res;
  } catch (error) {
    console.log(error)
    return [];
  }
}
